(() => {
  const storageKey = 'foxtrot:guest-kits:v1';
  const profileStorageKey = 'foxtrot:profile:v1';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let sharedCore;
  let sharedRegistry;
  let registryEntries = [];

  async function loadRegistry() {
    const coreUrl = document.querySelector('meta[name="foxtrot-classification-core"]')?.content;
    if (!coreUrl) throw new Error('The shared classification module URL is missing.');
    sharedCore = await import(new URL(coreUrl, location.href).href);
    let page = 1;
    const visited = new Set();
    while (page) {
      if (visited.has(page)) throw new Error('Classification pagination did not advance.');
      visited.add(page);
      const url = new URL(`${window.Shopify?.routes?.root || '/'}pages/10-cs`, location.origin);
      url.searchParams.set('view', 'foxtrot-classifications');
      url.searchParams.set('page', String(page));
      // JSON content negotiation bypasses alternate Liquid views on Shopify page and collection routes.
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Classification registry returned HTTP ${response.status}.`);
      const data = await response.json();
      if (!Array.isArray(data.entries)) throw new Error('The classification registry response is invalid.');
      registryEntries.push(...data.entries);
      page = data.nextPage;
    }
    if (!registryEntries.length) registryEntries = sharedCore.initialClassifications;
    registryEntries.forEach((entry) => {
      if (typeof entry.relationships === 'string') entry.relationships = JSON.parse(entry.relationships);
    });
    sharedRegistry = sharedCore.createRegistry(registryEntries);
  }

  function registryCodes(kind, values) {
    return sharedRegistry ? sharedRegistry.legacyCodes(kind, values) : values;
  }

  function normalizeProduct(product) {
    for (const [key, kind] of [['tenCs', 'ten_cs'], ['profiles', 'profiles'], ['capabilities', 'capabilities'], ['redundancy', 'redundancy']]) {
      if (!Array.isArray(product[key])) throw new Error(`Product ${product.id}: ${key} classifications must be a list.`);
      product[key] = registryCodes(kind, product[key]);
    }
    return product;
  }
  const defaultProfileLabels = {
    'thru-hiking': 'Thru Hiking',
    bushcraft: 'Bushcraft',
    'gray-man': 'Gray Man',
    operator: 'Operator'
  };

  function parseJson(script, label) {
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.error(`Foxtrot ${label} data is invalid JSON.`, error);
      return null;
    }
  }

  function parseProfileCodes(root) {
    try {
      const codes = JSON.parse(root.dataset.profileCodes || '[]');
      if (!Array.isArray(codes) || codes.some((code) => typeof code !== 'string')) throw new Error('Profile codes must be a string list.');
      return new Set(codes);
    } catch (error) {
      console.error('Foxtrot profile context is invalid.', error);
      return new Set();
    }
  }

  function initializeProfileContext(root) {
    const validProfiles = new Set(registryEntries.length
      ? registryEntries.filter(entry => entry.kind === 'profiles' && entry.active).map(entry => entry.code)
      : registryCodes('profiles', [...parseProfileCodes(root)]));
    const controls = Array.from(document.querySelectorAll('[data-fg-profile]'));
    controls.forEach((control) => {
      registryEntries.filter(entry => entry.kind === 'profiles' && entry.active).forEach((entry) => {
        if (Array.from(control.options).some(option => option.value === entry.code)) return;
        const option = document.createElement('option');
        option.value = entry.code;
        safeText(option, entry.label);
        control.append(option);
      });
    });
    const resolveProfile = (code) => registryCodes('profiles', [code])[0];
    const fromUrl = new URL(location.href).searchParams.get('use');
    let current = fromUrl && /^[a-z0-9]+(?:[-_][a-z0-9]+|__[a-z0-9]+)*$/.test(fromUrl)
      ? resolveProfile(fromUrl)
      : null;
    if (!current) {
      try {
        const stored = sessionStorage.getItem(profileStorageKey);
        if (stored && /^[a-z0-9]+(?:[-_][a-z0-9]+|__[a-z0-9]+)*$/.test(stored)) current = resolveProfile(stored);
      } catch (error) {
        console.error('Foxtrot could not read the temporary profile preference.', error);
      }
    }
    if (current && validProfiles.size && !validProfiles.has(current) && !defaultProfileLabels[current]) {
      console.error(`Foxtrot profile "${current}" is not present in the active taxonomy.`);
      current = null;
    }

    function syncLinks() {
      document.querySelectorAll('[data-fg-profile-button]').forEach((button) => {
        button.setAttribute('aria-pressed', String(resolveProfile(button.dataset.fgProfileButton) === current));
      });
      document.querySelectorAll('[data-fg-profile-image]').forEach((image, index) => {
        image.hidden = current ? image.dataset.fgProfileImage !== current : index !== 0;
      });
      document.querySelectorAll('a[href]').forEach((link) => {
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin) return;
        const relevant = /^\/(?:products|collections)\//.test(url.pathname)
          || /\/pages\/(?:10-cs|my-kit)\/?$/.test(url.pathname);
        if (!relevant || link.dataset.fgContextLink === 'false' || link.dataset.fgProfileFixed === 'true') return;
        if (current) {
          url.searchParams.set('use', current);
          link.dataset.fgAppliedProfile = current;
        } else if (link.dataset.fgAppliedProfile) {
          if (url.searchParams.get('use') === link.dataset.fgAppliedProfile) url.searchParams.delete('use');
          delete link.dataset.fgAppliedProfile;
        }
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }

    function applyProfile(code, { push = false, updateControls = true } = {}) {
      code = code ? resolveProfile(code) : null;
      if (code && validProfiles.size && !validProfiles.has(code) && !defaultProfileLabels[code]) return;
      current = code || null;
      try {
        if (current) sessionStorage.setItem(profileStorageKey, current);
        else sessionStorage.removeItem(profileStorageKey);
      } catch (error) {
        console.error('Foxtrot could not save the temporary profile preference.', error);
      }
      if (updateControls) controls.forEach((control) => {
        if (Array.from(control.options).some((option) => option.value === current)) control.value = current;
      });
      syncLinks();
      document.dispatchEvent(new CustomEvent('foxtrot:profile:change', { detail: { profile: current } }));
      if (push) {
        const url = new URL(location.href);
        if (current) url.searchParams.set('use', current);
        else url.searchParams.delete('use');
        history.pushState({ profile: current }, '', url);
      }
    }

    if (current) applyProfile(current, { updateControls: true });
    else {
      syncLinks();
      const selected = controls.find((control) => control.value);
      if (selected?.value) applyProfile(selected.value, { updateControls: false });
      else if (root.dataset.defaultProfile) applyProfile(root.dataset.defaultProfile);
    }
    history.replaceState({ ...(history.state || {}), profile: current }, '', location.href);
    controls.forEach((control) => {
      if (!root.contains(control)) return;
      const buttons = Array.from(root.querySelectorAll('[data-fg-profile-button]'));
      if (Array.from(control.options).every((option) => buttons.some((button) => button.dataset.fgProfileButton === option.value))) {
        control.closest('.fg-profile-picker').hidden = true;
      }
    });
    controls.forEach((control) => control.addEventListener('change', () => applyProfile(control.value, { push: true })));
    document.querySelectorAll('[data-fg-profile-button]').forEach((button) => button.addEventListener('click', () => {
      const control = root.querySelector('[data-fg-profile]');
      if (control) {
        control.value = resolveProfile(button.dataset.fgProfileButton);
        control.dispatchEvent(new Event('change', { bubbles: true }));
      } else applyProfile(button.dataset.fgProfileButton, { push: true });
    }));
    document.addEventListener('foxtrot:kit:profile', (event) => applyProfile(event.detail.profile, { push: true }));
    window.addEventListener('popstate', (event) => {
      const queryProfile = new URL(location.href).searchParams.get('use');
      const profile = queryProfile || (event.state && Object.hasOwn(event.state, 'profile') ? event.state.profile : null);
      applyProfile(profile, { updateControls: true });
    });
  }

  function safeText(element, value) {
    element.textContent = value == null ? '' : String(value);
    return element;
  }

  function makeId(prefix) {
    return `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
  }

  function initializeGuide(root) {
    const panels = Array.from(root.querySelectorAll('[data-guide-category]'));
    if (!panels.length) return;
    const links = Array.from(root.querySelectorAll('[data-fg-lesson-link]'));
    root.classList.add('is-interactive');
    root.querySelector('[data-fg-lesson-navigation]').hidden = false;
    let selected = panels[0];
    function select(code, navigate = false) {
      const panel = panels.find((item) => item.dataset.guideCategory === code) || panels[0];
      selected = panel;
      panels.forEach((item) => { item.hidden = item !== panel; });
      links.forEach((link) => {
        if (link.dataset.fgLessonLink === panel.dataset.guideCategory) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const tile = root.querySelector(`[data-fg-guide-tiles] a[aria-current="true"]`);
      const tabs = root.querySelector('[data-fg-guide-tiles]');
      if (tile && tabs && tabs.scrollWidth > tabs.clientWidth) {
        tabs.scrollTo({ left: tile.offsetLeft - tabs.offsetLeft - (tabs.clientWidth - tile.clientWidth) / 2, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      root.dispatchEvent(new CustomEvent('foxtrot:guide:lesson', { detail: { category: panel.dataset.guideCategory } }));
      if (navigate) {
        history.pushState({ ...(history.state || {}) }, '', `#${panel.id}`);
        panel.tabIndex = -1;
        panel.focus({ preventScroll: true });
        panel.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
    }
    root.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link) return;
      const destination = new URL(link.href, location.href);
      if (destination.origin !== location.origin || destination.pathname !== location.pathname) return;
      const panel = panels.find((item) => `#${item.id}` === destination.hash);
      if (!panel) return;
      event.preventDefault();
      select(panel.dataset.guideCategory, true);
    });
    root.querySelectorAll('[data-fg-lesson-step]').forEach((button) => button.addEventListener('click', () => {
      const index = (panels.indexOf(selected) + Number(button.dataset.fgLessonStep) + panels.length) % panels.length;
      select(panels[index].dataset.guideCategory, true);
    }));
    const fromHash = () => select(panels.find((panel) => `#${panel.id}` === location.hash)?.dataset.guideCategory);
    window.addEventListener('popstate', fromHash);
    window.addEventListener('hashchange', fromHash);
    fromHash();
  }

  function initializeGuideProducts(root) {
    const catalog = new Map();
    let requestVersion = 0;
    async function productsFor(path) {
      if (catalog.has(path)) return catalog.get(path);
      const request = (async () => {
        const products = [];
        let page = 1;
        const visited = new Set();
        while (page) {
          if (visited.has(page)) throw new Error('Product pagination did not advance.');
          visited.add(page);
          const url = new URL(path, location.origin);
          url.searchParams.set('view', 'foxtrot-kit-products');
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`Recommendations returned HTTP ${response.status}.`);
          const data = await response.json();
          if (!Array.isArray(data.products)) throw new Error('Product recommendations returned invalid data.');
          products.push(...data.products.map(normalizeProduct));
          page = data.nextPage;
        }
        return [...new Map(products.map(product => [product.id, product])).values()];
      })();
      catalog.set(path, request);
      try { return await request; }
      catch (error) { catalog.delete(path); throw error; }
    }
    async function update() {
      const version = ++requestVersion;
      const panel = root.querySelector('[data-guide-category]:not([hidden])');
      const container = panel?.querySelector('[data-fg-guide-recommendations]');
      if (!container) return;
      const list = container.querySelector('[data-fg-guide-products]');
      const status = container.querySelector('[data-fg-guide-no-products]');
      const all = container.querySelector('[data-fg-view-all-products]');
      const profile = root.querySelector('[data-fg-profile-button][aria-pressed="true"]')?.dataset.fgProfileButton;
      list.replaceChildren();
      all.hidden = true;
      status.hidden = false;
      safeText(status, 'Loading classified Shopify recommendations…');
      container.setAttribute('aria-busy', 'true');
      try {
        const products = await productsFor(container.dataset.collectionUrl);
        if (version !== requestVersion) return;
        const matches = sharedCore.recommendedProducts(products, sharedRegistry, panel.dataset.guideCategory, profile);
        function render(expanded) {
          list.replaceChildren();
          (expanded ? matches : matches.slice(0, 4)).forEach((product) => {
            const item = document.createElement('li');
            const link = document.createElement('a');
            const url = new URL(product.url, location.origin);
            url.searchParams.set('use', profile);
            link.href = `${url.pathname}${url.search}`;
            if (product.image) {
              const image = document.createElement('img');
              image.src = product.image;
              image.width = 240;
              image.height = 180;
              image.alt = '';
              image.loading = 'lazy';
              link.append(image);
            }
            const title = document.createElement('strong');
            safeText(title, product.title);
            const price = document.createElement('span');
            safeText(price, product.price);
            link.append(title, price);
            item.append(link);
            list.append(item);
          });
          all.setAttribute('aria-expanded', String(expanded));
          safeText(all, expanded ? 'Show fewer products' : `View all recommended products (${matches.length})`);
        }
        render(false);
        all.hidden = matches.length <= 4;
        all.onclick = () => render(all.getAttribute('aria-expanded') !== 'true');
        safeText(status, matches.length ? `${matches.length} matching products. Recommendations are guidance, not purchasing restrictions.`
          : `${container.dataset.emptyText} Browse all gear below; no capabilities are inferred from photographs.`);
      } catch (error) {
        if (version !== requestVersion) return;
        console.error('Foxtrot guide recommendations could not load.', error);
        safeText(status, 'Recommendations could not load. Change category or application to retry, or browse all gear below.');
      } finally {
        if (version === requestVersion) container.removeAttribute('aria-busy');
      }
    }
    root.addEventListener('foxtrot:guide:lesson', update);
    document.addEventListener('foxtrot:profile:change', update);
    update();
  }

  function initializeProductClassifications(root) {
    if (!root.dataset.variantListener) {
      root.dataset.variantListener = 'true';
      root.closest('foxtrot-product')?.addEventListener('foxtrot:variant:changed', () => initializeProductClassifications(root));
    }
    Array.from(root.children).filter(child => !child.matches('[data-fg-classified-product]')).forEach(child => child.remove());
    root.hidden = true;
    let product;
    try {
      const data = parseJson(root.querySelector('[data-fg-classified-product]'), 'product classification');
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Product classifications must be an object.');
      product = normalizeProduct(data);
    } catch (error) {
      console.error('Foxtrot product classifications could not initialize.', error);
      root.hidden = false;
      const notice = document.createElement('p');
      notice.setAttribute('role', 'alert');
      safeText(notice, 'Product classifications could not load. Reload or contact support; no capabilities have been assumed.');
      root.append(notice);
      return;
    }
    const entries = [];
    for (const [key, kind] of [['tenCs', 'ten_cs'], ['profiles', 'profiles']]) {
      product[key].forEach(value => {
        const entry = sharedRegistry.resolve(kind, value)?.entry;
        if (entry?.active) entries.push(entry);
      });
    }
    if (!entries.length) return;
    root.hidden = false;
    const title = document.createElement('h3');
    safeText(title, "10 C's categories and recommended applications");
    const list = document.createElement('ul');
    entries.forEach(entry => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const url = new URL(`${window.Shopify?.routes?.root || '/'}pages/10-cs`, location.origin);
      if (entry.kind === 'profiles') url.searchParams.set('use', entry.code);
      else url.hash = `guide-${entry.code.replaceAll('_', '-')}`;
      link.href = `${url.pathname}${url.search}${url.hash}`;
      if (entry.kind === 'profiles') link.dataset.fgProfileFixed = 'true';
      safeText(link, entry.label);
      item.append(link);
      list.append(item);
    });
    root.append(title, list);
  }

  async function initializeKit(root) {
    let savedPack = null;
    let taxonomy = parseJson(root.querySelector('[data-fg-taxonomy]'), 'taxonomy');
    const products = parseJson(root.querySelector('[data-fg-product-catalog]'), 'product catalog');
    const cartItems = parseJson(root.querySelector('[data-fg-cart-items]'), 'cart items');
    if (!Array.isArray(taxonomy) || !Array.isArray(products) || !Array.isArray(cartItems) || cartItems.some((item) =>
      !item || typeof item.title !== 'string' || !Array.isArray(item.tenCs)
      || typeof item.available !== 'boolean' || item.tenCs.some((code) => typeof code !== 'string') || !Array.isArray(item.capabilities)
      || item.capabilities.some((code) => typeof code !== 'string') || !Array.isArray(item.redundancy)
      || item.redundancy.some((code) => typeof code !== 'string'))) {
      root.querySelector('[data-fg-storage-status]').textContent = 'The checklist data could not be loaded. Reload the page or contact support.';
      root.querySelectorAll('button, input, select').forEach((control) => { control.disabled = true; });
      return;
    }
    if (taxonomy.length === 0 && root.dataset.taxonomyConfigured === 'false') {
      const coreUrl = document.querySelector('meta[name="foxtrot-classification-core"]')?.content;
      try {
        if (!coreUrl) throw new Error('The shared classification module URL is missing.');
        const core = await import(new URL(coreUrl, location.href).href);
        if (!Array.isArray(core.initialClassifications)) throw new Error('The shared classification module has no bootstrap taxonomy.');
        taxonomy = core.initialClassifications;
      } catch (error) {
        console.error('Foxtrot could not load the initial checklist taxonomy.', error);
        root.querySelector('[data-fg-storage-status]').textContent = 'The classification master is not available yet. Reload the page or contact support.';
        root.querySelectorAll('button, input, select').forEach((control) => { control.disabled = true; });
        return;
      }
    }
    taxonomy.forEach((item) => {
      if (typeof item.relationships !== 'string') return;
      try {
        item.relationships = JSON.parse(item.relationships);
      } catch (error) {
        console.error(`Foxtrot classification relationships are invalid for ${item.code}.`, error);
        item.relationships = {};
      }
    });
    taxonomy = registryEntries.filter(entry => entry.active).map(entry => {
      const relationships = { ...entry.relationships };
      for (const [key, kind] of [['requiredTenCs', 'ten_cs'], ['requiredCapabilities', 'capabilities'], ['capabilityCodes', 'capabilities']]) {
        if (Array.isArray(relationships[key])) relationships[key] = registryCodes(kind, relationships[key]);
      }
      if (Array.isArray(relationships.redundancy)) relationships.redundancy = relationships.redundancy.map(rule => ({ ...rule, capability: registryCodes('capabilities', [rule.capability])[0] }));
      return { ...entry, relationships };
    });
    products.forEach(normalizeProduct);
    cartItems.forEach(item => {
      item.tenCs = registryCodes('ten_cs', item.tenCs);
      item.capabilities = registryCodes('capabilities', item.capabilities);
      item.redundancy = registryCodes('redundancy', item.redundancy);
    });

    const active = (kind) => taxonomy.filter((item) => item.kind === kind);
    const byCode = new Map(taxonomy.map((item) => [item.code, item]));
    const options = {
      tenCs: active('ten_cs'),
      capabilities: active('capabilities'),
      redundancy: active('redundancy')
    };
    const storageStatus = root.querySelector('[data-fg-storage-status]');
    let state;
    try {
      const saved = sessionStorage.getItem(storageKey);
      state = saved ? JSON.parse(saved) : { version: 1, activeKitId: null, kits: [] };
      if (!state || state.version !== 1 || !Array.isArray(state.kits)
        || state.kits.some((kit) => !kit.id || !Array.isArray(kit.items))) throw new Error('The saved guest kit data is invalid.');
      state.kits.forEach(kit => kit.items.forEach(item => {
        item.tenCs = registryCodes('ten_cs', item.tenCs);
        item.capabilities = registryCodes('capabilities', item.capabilities);
        item.redundancy = registryCodes('redundancy', item.redundancy || []);
      }));
      state.kits.forEach(kit => { if (kit.profileCode) kit.profileCode = registryCodes('profiles', [kit.profileCode])[0]; });
    } catch (error) {
      console.error('Foxtrot could not read the temporary guest kit.', error);
      storageStatus.textContent = 'This session’s saved checklist could not be read. It was not replaced or reset.';
      root.querySelectorAll('button, input, select').forEach((control) => { control.disabled = true; });
      return;
    }

    function persist() {
      if (savedPack) return true;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
        storageStatus.textContent = '';
        return true;
      } catch (error) {
        console.error('Foxtrot could not save the temporary guest kit.', error);
        storageStatus.textContent = 'This browser could not save the checklist for this session. Your latest changes may be lost if you leave this page.';
        return false;
      }
    }

    if (!state.kits.length) {
      const kit = { version: 1, id: makeId('kit'), name: 'My Kit', profileCode: null, items: [] };
      state.kits.push(kit);
      state.activeKitId = kit.id;
    }
    let selectedProfile = null;
    const profileSelect = root.querySelector('[data-fg-profile]');
    const kitSelect = root.querySelector('[data-fg-kit-select]');
    const nameInput = root.querySelector('[data-fg-kit-name]');
    const form = root.querySelector('[data-fg-add-item]');
    const kitItems = root.querySelector('[data-fg-kit-items]');
    const coverage = root.querySelector('[data-fg-coverage]');
    const capabilityGaps = root.querySelector('[data-fg-capability-gaps]');
    const redundancyGaps = root.querySelector('[data-fg-redundancy-gaps]');
    const recommendations = root.querySelector('[data-fg-recommendations]');
    const categoryCards = root.querySelector('[data-fg-category-cards]');
    const categoryImages = parseJson(root.querySelector('[data-fg-category-images]'), 'category images') || {};
    taxonomy.filter(item => item.kind === 'ten_cs' && item.relationships.imageUrl).forEach(item => { categoryImages[item.code] = item.relationships.imageUrl; });
    const editor = root.querySelector('[data-fg-item-editor]');
    const finder = root.querySelector('[data-fg-product-search]');
    const productScope = root.querySelector('[data-fg-product-scope]');
    const productCategory = root.querySelector('[data-fg-product-category]');
    const searchStatus = root.querySelector('[data-fg-search-status]');
    const moreProducts = root.querySelector('[data-fg-more-products]');
    let editingId = null;
    let searchProducts = null;
    let nextSearchPage = null;
    let searchQuery = '';
    let searchRequest = null;
    options.tenCs.forEach((category) => {
      const option = document.createElement('option');
      option.value = category.code;
      safeText(option, category.label);
      productCategory.append(option);
    });

    function currentKit() {
      if (savedPack) return savedPack;
      return state.kits.find((kit) => kit.id === state.activeKitId) || state.kits[0];
    }

    function renderCheckboxes(container, key, values) {
      container.replaceChildren();
      values.forEach((value, index) => {
        const id = `${key}-${index}`;
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = value.code;
        input.id = id;
        label.htmlFor = id;
        label.append(input, document.createTextNode(` ${value.label}`));
        container.append(label);
      });
    }

    renderCheckboxes(root.querySelector('[data-fg-ten-cs]'), 'kit-ten-c', options.tenCs);
    renderCheckboxes(root.querySelector('[data-fg-capabilities]'), 'kit-capability', options.capabilities);
    renderCheckboxes(root.querySelector('[data-fg-redundancy]'), 'kit-redundancy', options.redundancy);
    root.classList.add('is-interactive');

    function renderItems() {
      const kit = currentKit();
      kitItems.replaceChildren();
      kit.items.forEach((item) => {
        const row = document.createElement('tr');
        const details = document.createElement('td');
        const thumbnail = item.image || categoryImages[item.tenCs[0]];
        if (thumbnail) {
          const image = document.createElement('img');
          image.src = thumbnail;
          image.alt = item.image ? '' : 'Category illustration';
          image.width = 46;
          image.height = 38;
          image.loading = 'lazy';
          details.append(image);
        }
        const title = document.createElement('strong');
        safeText(title, item.title);
        const summary = document.createElement('td');
        const status = document.createElement('td');
        safeText(status, 'Owned');
        const labels = [...item.tenCs, ...item.capabilities]
          .map((code) => byCode.get(code)?.label || code.replaceAll('-', ' '));
        safeText(summary, labels.length ? labels.join(' · ') : 'No classifications assigned');
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.removeItem = item.id;
        safeText(remove, 'Remove');
        remove.setAttribute('aria-label', `Remove ${item.title}`);
        const edit = document.createElement('button');
        edit.type = 'button';
        safeText(edit, 'Edit');
        edit.setAttribute('aria-label', `Edit ${item.title}`);
        edit.addEventListener('click', () => {
          editingId = item.id;
          editor.open = true;
          form.elements.namedItem('title').value = item.title;
          for (const [selector, values] of [['ten-cs', item.tenCs], ['capabilities', item.capabilities], ['redundancy', item.redundancy]]) {
            form.querySelectorAll(`[data-fg-${selector}] input`).forEach((input) => { input.checked = values.includes(input.value); });
          }
          form.querySelector('[type="submit"]').textContent = 'Save equipment';
          form.elements.namedItem('title').focus();
        });
        const actions = document.createElement('td');
        actions.append(edit, remove);
        if (savedPack) {
          actions.replaceChildren();
          const manage = document.createElement('a');
          manage.href = '#my-gear';
          safeText(manage, 'Manage in My Packs');
          actions.append(manage);
        }
        details.append(title);
        row.append(details, summary, status, actions);
        kitItems.append(row);
      });
      cartItems.forEach((item) => {
        const row = document.createElement('tr');
        const details = document.createElement('td');
        const title = document.createElement('strong');
        safeText(title, item.title);
        const status = document.createElement('td');
        safeText(status, `In Cart${item.available ? '' : ' · Unavailable'} · Quantity ${item.quantity}`);
        const summary = document.createElement('td');
        const labels = [...item.tenCs, ...item.capabilities]
          .map((code) => byCode.get(code)?.label || code.replaceAll('-', ' '));
        safeText(summary, labels.length ? labels.join(' · ') : 'No reviewed classifications assigned');
        details.append(title);
        const actions = document.createElement('td');
        const cartLink = document.createElement('a');
        cartLink.href = `${window.Shopify?.routes?.root || '/'}cart`;
        safeText(cartLink, 'View cart');
        actions.append(cartLink);
        row.append(details, summary, status, actions);
        kitItems.append(row);
      });
      kitItems.querySelectorAll('[data-remove-item]').forEach((button) => button.addEventListener('click', () => {
        if (editingId === button.dataset.removeItem) resetEditor();
        kit.items = kit.items.filter((item) => item.id !== button.dataset.removeItem);
        persist();
        render();
      }));
      if (!kitItems.children.length) {
        const row = document.createElement('tr');
        const cell = document.createElement('td');
        cell.colSpan = 4;
        safeText(cell, 'No equipment entered yet. Add gear you own or find products below.');
        row.append(cell);
        kitItems.append(row);
      }
    }

    function renderCoverage() {
      const kit = currentKit();
      const ownedTenCs = new Set(kit.items.flatMap((item) => item.tenCs));
      const availableCartItems = cartItems.filter((item) => item.available);
      const cartTenCs = new Set(availableCartItems.flatMap((item) => item.tenCs));
      const ownedCount = options.tenCs.filter((item) => ownedTenCs.has(item.code)).length;
      const cartCount = options.tenCs.filter((item) => !ownedTenCs.has(item.code) && cartTenCs.has(item.code)).length;
      root.querySelector('[data-fg-progress]').style.setProperty('--fg-progress', `${options.tenCs.length ? ownedCount / options.tenCs.length * 100 : 0}%`);
      root.querySelector('[data-fg-progress]').setAttribute('aria-label', `${ownedCount} of ${options.tenCs.length} categories covered by owned equipment`);
      safeText(root.querySelector('[data-fg-progress-label]'), `${ownedCount} / ${options.tenCs.length}`);
      safeText(root.querySelector('[data-fg-total="owned"]'), ownedCount);
      safeText(root.querySelector('[data-fg-total="cart"]'), cartCount);
      safeText(root.querySelector('[data-fg-total="missing"]'), options.tenCs.length - ownedCount - cartCount);
      const openCategories = new Set(Array.from(categoryCards.querySelectorAll('details[open]'), (item) => item.dataset.category));
      categoryCards.replaceChildren();
      coverage.replaceChildren();
      options.tenCs.forEach((item, index) => {
        const row = document.createElement('li');
        const state = ownedTenCs.has(item.code) ? 'Owned' : cartTenCs.has(item.code) ? 'In Cart' : 'Missing';
        row.className = `is-${state.toLowerCase().replaceAll(' ', '-')}`;
        const label = document.createElement('span');
        safeText(label, item.label);
        const status = document.createElement('span');
        safeText(status, root.classList.contains('fg-workbench-connected') && state === 'Owned' ? 'Covered' : state);
        row.append(label, status);
        coverage.append(row);
        const card = document.createElement('details');
        card.className = `fg-kit-category ${row.className}`;
        card.dataset.category = item.code;
        card.open = openCategories.has(item.code);
        const summary = document.createElement('summary');
        const heading = document.createElement('strong');
        safeText(heading, `${index + 1}  ${item.label}`);
        const badge = document.createElement('span');
        badge.className = 'fg-kit-badge';
        safeText(badge, state);
        const image = document.createElement('img');
        image.alt = `${item.label} category illustration`;
        image.width = 112;
        image.height = 96;
        image.loading = 'lazy';
        const matching = [...kit.items, ...availableCartItems].filter((entry) => entry.tenCs.includes(item.code));
        const names = document.createElement('span');
        names.className = 'fg-kit-category__gear';
        safeText(names, matching.length ? matching.map((entry) => entry.title).join(' · ') : 'No equipment recorded');
        summary.append(heading);
        if (categoryImages[item.code]) {
          image.src = categoryImages[item.code];
          summary.append(image);
        }
        summary.append(badge, names);
        const body = document.createElement('div');
        body.className = 'fg-kit-category__detail';
        const learn = document.createElement('a');
        learn.href = `${window.Shopify?.routes?.root || '/'}pages/10-cs?use=${encodeURIComponent(selectedProfile || '')}#guide-${item.code}`;
        safeText(learn, 'Learn why →');
        const find = document.createElement('button');
        find.type = 'button';
        safeText(find, 'Find equipment');
        find.addEventListener('click', () => {
          productCategory.value = item.code;
          renderRecommendations();
          finder.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
        body.append(learn, find);
        card.append(summary, body);
        categoryCards.append(card);
      });

      const profile = byCode.get(selectedProfile);
      const requiredCapabilities = Array.isArray(profile?.relationships?.requiredCapabilities) ? profile.relationships.requiredCapabilities : [];
      const requiredTenCs = Array.isArray(profile?.relationships?.requiredTenCs) ? profile.relationships.requiredTenCs : [];
      const ownedCapabilities = new Set(kit.items.flatMap((item) => item.capabilities));
      const cartCapabilities = new Set(availableCartItems.flatMap((item) => item.capabilities));
      const coveredCapabilities = new Set([...ownedCapabilities, ...cartCapabilities]);
      const coveredTenCs = new Set([...ownedTenCs, ...cartTenCs]);
      const missing = requiredCapabilities.filter((code) => !coveredCapabilities.has(code));
      const missingCs = requiredTenCs.filter((code) => !coveredTenCs.has(code));
      capabilityGaps.replaceChildren();
      if (!requiredCapabilities.length && !requiredTenCs.length) {
        safeText(capabilityGaps, 'Mission requirements are not configured for this profile yet. The guide remains available, but this checklist will not invent missing gear.');
      } else if (!missing.length && !missingCs.length) {
        safeText(capabilityGaps, 'All currently configured mission requirements are represented by owned or in-cart items.');
      } else {
        const list = document.createElement('ul');
        [...missingCs.map((code) => byCode.get(code)?.label || code), ...missing.map((code) => byCode.get(code)?.label || code)].forEach((label) => {
          const item = document.createElement('li');
          safeText(item, label);
          list.append(item);
        });
        capabilityGaps.append(list);
      }

      redundancyGaps.replaceChildren();
      const rules = Array.isArray(profile?.relationships?.redundancy) ? profile.relationships.redundancy : [];
      let backupGaps = 0;
      if (!rules.length) {
        safeText(redundancyGaps, 'Independent backup requirements are not configured for this profile yet.');
      } else {
        const list = document.createElement('ul');
        rules.forEach((rule) => {
          const requirement = Number(rule.minimumIndependentMethods);
          const capability = byCode.get(rule.capability)?.label || rule.capability;
          const methods = new Set(kit.items.flatMap((item) => {
            if (!item.capabilities.includes(rule.capability)) return [];
            return item.redundancy.filter((code) => {
              const relation = byCode.get(code)?.relationships;
              return Array.isArray(relation?.capabilityCodes) && relation.capabilityCodes.includes(rule.capability);
            });
          }));
          const line = document.createElement('li');
          const state = methods.size < requirement ? 'Needs backup' : 'Backup represented';
          if (methods.size >= requirement && methods.size > 1) {
            const categories = new Set(kit.items.filter(item => item.capabilities.includes(rule.capability)).flatMap(item => item.tenCs));
            coverage.querySelectorAll('li').forEach((row, index) => {
              if (!categories.has(options.tenCs[index].code) || !ownedTenCs.has(options.tenCs[index].code)) return;
              row.className = 'is-redundant'; safeText(row.lastElementChild, 'Redundant');
            });
          }
          if (methods.size < requirement) backupGaps += 1;
          if (methods.size < requirement) {
            const affected = new Set(kit.items.filter((item) => item.capabilities.includes(rule.capability)).flatMap((item) => item.tenCs));
            categoryCards.querySelectorAll('details').forEach((card) => {
              if (!affected.has(card.dataset.category)) return;
              const badge = document.createElement('span');
              badge.className = 'fg-kit-backup';
              safeText(badge, 'Needs Backup');
              card.querySelector('summary .fg-kit-category__gear').prepend(badge, document.createElement('br'));
            });
          }
          safeText(line, `${state} — ${capability}: ${methods.size} of ${requirement} distinct method classifications entered`);
          list.append(line);
        });
        redundancyGaps.append(list);
      }
      safeText(root.querySelector('[data-fg-total="backup"]'), rules.length ? backupGaps : 'Not configured');
    }

    function renderRecommendations() {
      recommendations.replaceChildren();
      const kit = currentKit();
      const profile = byCode.get(selectedProfile);
      const requiredCapabilities = Array.isArray(profile?.relationships?.requiredCapabilities) ? profile.relationships.requiredCapabilities : [];
      const requiredTenCs = Array.isArray(profile?.relationships?.requiredTenCs) ? profile.relationships.requiredTenCs : [];
      const ownedCapabilities = new Set(kit.items.flatMap((item) => item.capabilities));
      const ownedTenCs = new Set(kit.items.flatMap((item) => item.tenCs));
      const availableCartItems = cartItems.filter((item) => item.available);
      const inCartCapabilities = new Set(availableCartItems.flatMap((item) => item.capabilities));
      const inCartTenCs = new Set(availableCartItems.flatMap((item) => item.tenCs));
      const coveredCapabilities = new Set([...ownedCapabilities, ...inCartCapabilities]);
      const coveredTenCs = new Set([...ownedTenCs, ...inCartTenCs]);
      const missingCapabilities = requiredCapabilities.filter((code) => !coveredCapabilities.has(code));
      const missingTenCs = requiredTenCs.filter((code) => !coveredTenCs.has(code));
      const candidates = (searchProducts || products).filter((product) =>
        (productScope.value === 'all' || (Array.isArray(product.profiles) && product.profiles.includes(selectedProfile)))
        && (!productCategory.value || (product.tenCs || []).includes(productCategory.value))
        && (productScope.value !== 'gaps' || (product.available === true
          && ((product.capabilities || []).some((code) => missingCapabilities.includes(code))
            || (product.tenCs || []).some((code) => missingTenCs.includes(code))))))
        .sort((left, right) => Number(right.priority || 0) - Number(left.priority || 0) || left.title.localeCompare(right.title));
      searchStatus.textContent = searchProducts
        ? `${candidates.length} matching products in loaded ${searchQuery ? 'search' : 'catalog'} results.${nextSearchPage ? ' More products are available.' : ''}`
        : `Browsing ${products.length} products from the configured collection or initial catalog pool. Search to find products beyond this pool.`;
      if (!candidates.length) {
        safeText(recommendations, 'No products match these filters. Search the store or choose All products to include unclassified gear. Unclassified gear never counts toward capability coverage.');
        return;
      }
      const visibleCandidates = searchProducts ? candidates : candidates.slice(0, 24);
      visibleCandidates.forEach((product) => {
        const card = document.createElement('article');
        card.className = 'fg-kit-product';
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          card.append(image);
        }
        const title = document.createElement('a');
        const productUrl = new URL(product.url, location.href);
        if (selectedProfile) productUrl.searchParams.set('use', selectedProfile);
        title.href = `${productUrl.pathname}${productUrl.search}${productUrl.hash}`;
        safeText(title, product.title);
        const price = document.createElement('p');
        safeText(price, product.price);
        const close = [...(product.capabilities || []).filter((code) => missingCapabilities.includes(code)), ...(product.tenCs || []).filter((code) => missingTenCs.includes(code))]
          .map((code) => byCode.get(code)?.label || code);
        const reason = document.createElement('p');
        const classified = (product.tenCs || []).length || (product.capabilities || []).length;
        safeText(reason, close.length ? `Addresses: ${[...new Set(close)].join(', ')}` : classified
          ? `Classified: ${(product.tenCs || []).map((code) => byCode.get(code)?.label || code).join(', ')}`
          : 'Unclassified product. No capability coverage will be assumed.');
        const add = document.createElement('button');
        add.type = 'submit';
        add.className = 'fg-button fg-button--outline fg-button--small';
        if (product.available && !product.requiresSellingPlan && product.hasOnlyDefaultVariant) {
          add.dataset.addVariant = product.variantId;
          add.dataset.productId = product.id;
          safeText(add, 'Add to cart');
        } else {
          add.disabled = true;
          safeText(add, product.available ? 'Choose options on product page' : 'Sold out');
        }
        const own = document.createElement('button');
        own.type = 'button';
        own.className = 'fg-kit-text-button';
        const alreadyOwned = kit.items.some((item) => String(item.productId) === String(product.id));
        own.disabled = alreadyOwned || !product.hasOnlyDefaultVariant;
        safeText(own, alreadyOwned ? 'Recorded as owned' : product.hasOnlyDefaultVariant ? 'I own this' : 'Choose a variant on product page');
        own.addEventListener('click', () => {
          if (savedPack) {
            const { weightOz, ...measurements } = product.packability || {};
            document.dispatchEvent(new CustomEvent('foxtrot:customer:record-product', { detail: {
              productId: String(product.id), variantId: String(product.variantId), title: product.title, variantTitle: 'Default Title', quantity: 1,
              ...(product.fgId ? { fgId: product.fgId, gearDefinitionId: product.gearDefinitionId, equipmentName: product.equipmentName } : {}),
              ...(product.manufacturer ? { manufacturer: product.manufacturer } : {}),
              ...(product.model ? { model: product.model } : {}),
              ...(product.gtin ? { gtin: product.gtin } : {}),
              ...(product.image ? { imageUrl: new URL(product.image, location.href).href } : {}),
              packability: Object.fromEntries(Object.entries(measurements).filter(([, value]) => value !== null)),
              ...(weightOz == null ? {} : { weightOz }), tenCs: product.tenCs || [], capabilities: product.capabilities || [], redundancy: product.redundancy || []
            } }));
            return;
          }
          kit.items.push({ id: makeId('item'), title: product.title, productId: product.id, variantId: product.variantId,
            fgId: product.fgId, gearDefinitionId: product.gearDefinitionId,
            packability: product.packability,
            image: product.image, tenCs: product.tenCs || [], capabilities: product.capabilities || [],
            redundancy: product.redundancy || [], source: 'owned', quantity: 1 });
          render();
        });
        const cartForm = document.createElement('form');
        cartForm.method = 'post';
        cartForm.action = `${window.Shopify?.routes?.root || '/'}cart/add`;
        const variant = document.createElement('input');
        variant.type = 'hidden';
        variant.name = 'id';
        variant.value = product.variantId;
        const quantity = document.createElement('input');
        quantity.type = 'hidden';
        quantity.name = 'quantity';
        quantity.value = '1';
        cartForm.append(variant, quantity, add);
        card.append(title, price, reason, cartForm, own);
        recommendations.append(card);
      });
    }

    async function searchCatalog(append = false) {
      const query = finder.elements.namedItem('q').value.trim();
      if (query && query.length < 2) {
        searchStatus.textContent = 'Enter at least two characters, or use Browse kit products.';
        return;
      }
      searchRequest?.abort();
      searchRequest = null;
      finder.removeAttribute('aria-busy');
      const request = new AbortController();
      searchRequest = request;
      const queryChanged = query !== searchQuery;
      const page = append && !queryChanged ? nextSearchPage : 1;
      if (!page) return;
      moreProducts.disabled = true;
      searchStatus.textContent = 'Searching Shopify products…';
      finder.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(`${window.Shopify?.routes?.root || '/'}${query ? 'search' : 'collections/all'}`, location.origin);
        if (query) {
          url.searchParams.set('q', query);
          url.searchParams.set('type', 'product');
        }
        url.searchParams.set('view', 'foxtrot-kit-products');
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Product search returned HTTP ${response.status}.`);
        const data = await response.json();
        if (!Array.isArray(data.products) || data.products.some((product) => !product.id
          || typeof product.title !== 'string' || !Array.isArray(product.tenCs)
          || !Array.isArray(product.profiles) || !Array.isArray(product.capabilities)
          || !Array.isArray(product.redundancy)) || data.nextPage !== null &&
          (!Number.isInteger(data.nextPage) || data.nextPage <= page)) throw new Error('Shopify search returned invalid product classification data or pagination.');
        if (searchRequest !== request) return;
        searchQuery = query;
        data.products.forEach(normalizeProduct);
        searchProducts = append && !queryChanged ? [...(searchProducts || []), ...data.products] : data.products;
        searchProducts = [...new Map(searchProducts.map((product) => [product.id, product])).values()];
        data.products.forEach((product) => {
          const index = products.findIndex((item) => item.id === product.id);
          if (index < 0) products.push(product);
          else products[index] = product;
        });
        nextSearchPage = data.nextPage;
        moreProducts.hidden = !nextSearchPage;
        renderRecommendations();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Foxtrot kit product search failed.', error);
        searchStatus.textContent = 'Product search could not load. Please retry or browse the store directly.';
      } finally {
        if (searchRequest === request) {
          finder.removeAttribute('aria-busy');
          moreProducts.disabled = false;
        }
      }
    }
    finder.addEventListener('submit', (event) => { event.preventDefault(); searchCatalog(); });
    moreProducts.addEventListener('click', () => searchCatalog(true));
    productScope.addEventListener('change', renderRecommendations);
    productCategory.addEventListener('change', renderRecommendations);
    root.querySelector('[data-fg-browse-products]').addEventListener('click', () => {
      finder.elements.namedItem('q').value = '';
      void searchCatalog();
    });
    root.querySelectorAll('[data-fg-expand-categories]').forEach((button) => button.addEventListener('click', () => {
      categoryCards.querySelectorAll('details').forEach((card) => { card.open = button.dataset.fgExpandCategories === 'true'; });
    }));
    function resetEditor() {
      editingId = null;
      form.reset();
      form.querySelector('[type="submit"]').textContent = 'Add to my kit';
    }
    root.querySelector('[data-fg-cancel-edit]').addEventListener('click', () => { resetEditor(); editor.open = false; });

    function render() {
      const kit = currentKit();
      selectedProfile = savedPack ? kit.profileCode || null : profileSelect?.value || kit.profileCode || null;
      if (!savedPack && kit.profileCode !== selectedProfile) kit.profileCode = selectedProfile;
      kitSelect.replaceChildren();
      state.kits.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.id;
        safeText(option, item.name);
        kitSelect.append(option);
      });
      kitSelect.value = kit.id;
      nameInput.value = kit.name;
      renderItems();
      renderCoverage();
      renderRecommendations();
      persist();
    }

    const cartMetadata = new Map(cartItems.map(item => [String(item.variantId), item]));
    function syncCart(cart) {
      cartItems.splice(0, cartItems.length, ...cart.items.map(line => {
        const product = products.find(item => String(item.id) === String(line.product_id));
        const metadata = product?.variantEquipment?.find(item => String(item.variantId) === String(line.variant_id)) ||
          cartMetadata.get(String(line.variant_id)) || (String(product?.variantId) === String(line.variant_id) ? product : undefined);
        return {
          productId: line.product_id, variantId: line.variant_id, title: line.product_title || line.title, quantity: line.quantity,
          available: metadata?.available === true, tenCs: metadata?.tenCs || [],
          capabilities: metadata?.capabilities || [], redundancy: metadata?.redundancy || []
        };
      }));
      render();
    }
    document.addEventListener('foxtrot:cart:updated', event => syncCart(event.detail));
    document.addEventListener('foxtrot:customer:pack-selected', event => {
      savedPack = event.detail;
      savedPack.items.forEach(item => {
        item.tenCs = registryCodes('ten_cs', item.tenCs);
        item.capabilities = registryCodes('capabilities', item.capabilities);
        item.redundancy = registryCodes('redundancy', item.redundancy);
      });
      root.classList.add('has-saved-pack');
      profileSelect.disabled = true;
      if (savedPack.profileCode) document.dispatchEvent(new CustomEvent('foxtrot:kit:profile', { detail: { profile: savedPack.profileCode } }));
      render();
    });
    document.addEventListener('foxtrot:customer:signed-out', () => { savedPack = null; profileSelect.disabled = false; root.classList.remove('has-saved-pack'); render(); });
    document.dispatchEvent(new Event('foxtrot:customer:request-pack'));
    if (window.FoxtrotCart?.cart) syncCart(window.FoxtrotCart.cart);
    document.addEventListener('foxtrot:customer:guest-merged', () => {
      const kit = { version: 1, id: makeId('kit'), name: 'My Kit', profileCode: selectedProfile, items: [] };
      state = { version: 1, activeKitId: kit.id, kits: [kit] };
      resetEditor();
      render();
      storageStatus.textContent = 'Your temporary kit was saved to your Shopify customer account. Start another temporary plan whenever you like.';
    });

    kitSelect.addEventListener('change', () => {
      state.activeKitId = kitSelect.value;
      const kit = currentKit();
      resetEditor();
      if (kit.profileCode && Array.from(profileSelect.options).some((option) => option.value === kit.profileCode)) {
        document.dispatchEvent(new CustomEvent('foxtrot:kit:profile', { detail: { profile: kit.profileCode } }));
      }
      render();
    });
    nameInput.addEventListener('change', () => {
      const value = nameInput.value.trim();
      if (!value) {
        nameInput.value = currentKit().name;
        storageStatus.textContent = 'Kit names cannot be empty.';
        return;
      }
      currentKit().name = value.slice(0, 80);
      if (!root.classList.contains('fg-kit--workbench')) persist();
      render();
    });
    profileSelect?.addEventListener('change', () => {
      currentKit().profileCode = profileSelect.value;
      document.dispatchEvent(new CustomEvent('foxtrot:profile:change', { detail: { profile: profileSelect.value } }));
      render();
    });
    window.addEventListener('popstate', () => render());
    root.querySelector('[data-fg-create-kit]').addEventListener('click', () => {
      const kit = { version: 1, id: makeId('kit'), name: `Kit ${state.kits.length + 1}`, profileCode: selectedProfile, items: [] };
      state.kits.push(kit);
      state.activeKitId = kit.id;
      resetEditor();
      persist();
      render();
    });
    root.querySelector('[data-fg-delete-kit]').addEventListener('click', () => {
      if (state.kits.length < 2) {
        storageStatus.textContent = 'Keep at least one kit in this session.';
        return;
      }
      if (!confirm('Delete this temporary kit from the current browser session?')) return;
      state.kits = state.kits.filter((kit) => kit.id !== state.activeKitId);
      state.activeKitId = state.kits[0].id;
      resetEditor();
      persist();
      render();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const title = new FormData(form).get('title')?.toString().trim();
      const tenCs = Array.from(form.querySelectorAll('[data-fg-ten-cs] input:checked'), (input) => input.value);
      const capabilities = Array.from(form.querySelectorAll('[data-fg-capabilities] input:checked'), (input) => input.value);
      const redundancy = Array.from(form.querySelectorAll('[data-fg-redundancy] input:checked'), (input) => input.value);
      if (!title || (!tenCs.length && !capabilities.length && !redundancy.length)) {
        storageStatus.textContent = 'Enter an equipment name and at least one classification before adding it.';
        return;
      }
      const existing = editingId ? currentKit().items.find((item) => item.id === editingId) : null;
      if (existing) Object.assign(existing, { title: title.slice(0, 120), tenCs, capabilities, redundancy });
      else if (editingId) {
        storageStatus.textContent = 'This equipment is no longer in the current kit. Cancel editing and select it again.';
        return;
      } else currentKit().items.push({ id: makeId('item'), title: title.slice(0, 120), tenCs, capabilities, redundancy, source: 'owned', quantity: 1 });
      resetEditor();
      editor.open = false;
      persist();
      render();
      kitItems.lastElementChild?.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });
    if (state.activeKitId && state.kits.some((kit) => kit.id === state.activeKitId)) {
      const initialKit = currentKit();
      if (initialKit.profileCode && Array.from(profileSelect.options).some((option) => option.value === initialKit.profileCode)) {
        document.dispatchEvent(new CustomEvent('foxtrot:kit:profile', { detail: { profile: initialKit.profileCode } }));
      }
    }
    persist();
    render();
    finder.querySelectorAll('input,select,button').forEach(control => { control.disabled = false; });
    finder.removeAttribute('aria-busy');
    root.dataset.fgPlanningReady = 'true';
  }

  async function initialize() {
    try {
      await loadRegistry();
    } catch (error) {
      console.error('Foxtrot classification registry could not initialize.', error);
      document.querySelectorAll('.fg-field-guide, [data-fg-kit], [data-fg-product-classifications]').forEach(root => {
        const notice = document.createElement('p');
        notice.setAttribute('role', 'alert');
        notice.className = 'fg-editor-note';
        safeText(notice, 'The classification registry could not load. Reload to retry. No recommendations or kit changes have been assumed.');
        root.hidden = false;
        root.prepend(notice);
        root.querySelectorAll('[data-fg-profile-button]').forEach(control => { control.disabled = true; });
        if (root.matches('[data-fg-kit]')) root.querySelectorAll('button, input, select').forEach(control => { control.disabled = true; });
      });
      document.querySelectorAll('.fg-field-guide').forEach(initializeGuide);
      return;
    }
    const profileRoot = document.querySelector('main [data-fg-profile-context]') || document.querySelector('[data-fg-profile-context]');
    if (profileRoot) initializeProfileContext(profileRoot);
    document.querySelectorAll('.fg-field-guide').forEach(root => { initializeGuide(root); initializeGuideProducts(root); });
    document.querySelectorAll('[data-fg-product-classifications]').forEach(initializeProductClassifications);
    for (const root of document.querySelectorAll('[data-fg-kit]')) {
      try { await initializeKit(root); }
      catch (error) {
        console.error('Foxtrot checklist could not initialize.', error);
        safeText(root.querySelector('[data-fg-storage-status]'), 'The checklist could not load. Its saved draft was not replaced. Reload or contact support.');
        root.querySelectorAll('button, input, select').forEach(control => { control.disabled = true; });
      }
    }
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || !(event.target instanceof Element)) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.href, location.href);
      const isAnchor = destination.origin === location.origin && destination.pathname === location.pathname
        && destination.hash && document.querySelector(destination.hash);
      if (!isAnchor) return;
      event.preventDefault();
      const target = document.querySelector(destination.hash);
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
      history.pushState({ ...(history.state || {}) }, '', destination.hash);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
